import {
  AdditionalFundsData,
  ApplicationStatusUpdateResponse,
  CreateApplicationData,
  CreateApplicationResponse,
  Employer,
  GetApplicationAprResponse,
  GetApplicationDataResponse,
  Tradeline,
} from 'handlers/applicationData';
import { BankAccountDataForLos } from 'handlers/bankAccount';
import { ApplicationStatusName } from 'enums/ApplicationStatusName';
import { PaymentMethod } from 'enums/PaymentMethod';
import { CurrentFlow } from 'enums/CurrentFlow';
import { PayFrequencyOptionValue } from 'handlers/yourTotalIncome';

import Api, { ErrorMode, FetchMethodType } from './Api';

export interface DataForUpdatingBankAccount {
  applicationId: string;
  bankAccountData: BankAccountDataForLos;
}

export interface DataForUpdatingApplicationStatus {
  applicationId: string;
  applicationStatus?: ApplicationStatusName;
  applicationFlow?: CurrentFlow;
}

export interface DataForUpdatingApplicationFeedback {
  applicationId: string;
  experience?: string;
  howDidYouHear?: string;
}

export interface DataForPatchingIdentity {
  applicationId: string;
  citizenship?: string;
  socialSecurityNumber?: string;
}

export interface DataForPatchingFlow {
  applicationId: string;
  currentFlow: CurrentFlow;
}

export interface DataForUpdatingApplicationLoanManagement {
  applicationId: string;
  missedPaymentReason?: string;
}

export enum TradelineNewState {
  Selected = 'selected',
  Unselected = 'unselected',
}

export type SelectedTradeline = { tradeline: Tradeline; newState: TradelineNewState };

export interface DataForUpdatingTradelines {
  applicationId: string;
  tradelinesToUpdate: SelectedTradeline[];
}

export interface DataForUpdatingAdditionalFunds {
  applicationId: string;
  additionalFundsAmount: number;
}

export interface DataForUpdatingPaymentMethod {
  applicationId: string;
  paymentMethod: PaymentMethod;
}

export interface EmploymentData {
  employment: Employer[];
  isLicensedProfessional?: boolean;
  additionalIncome?: number;
  healthcareLicenseNumber?: string;
  healthcareLicenseState?: string;
}

export interface UpdatedEmploymentData {
  applicationId: string;
  employmentData: EmploymentData;
}

export interface LatestLoanPaymentResponse {
  amount: number;
  date: string;
}

export interface UpcomingLoanPayment {
  id: string;
  type: string;
  period: number;
  chargeAmount: number;
  date: string;
  title: string;
}

export interface GetUpcomingLoanPaymentsResponse {
  upcomingPayments: UpcomingLoanPayment[];
}
export interface NextAutopay {
  id: number;
  status: string;
  applyDate: string;
  amount: number;
  type: string;
  recurringFrequency: string;
}

export interface LoanBreakdownItem {
  applicationId: string;
  id: number;
  dateOfLoan: string;
  amountDue: number;
  nextPaymentDate: string | null;
  nextPaymentAmount: number;
  principalBalance: number;
  periodsRemaining: number;
  totalLoanAmount: number;
  totalPayments: number;
  payoffAmount: number;
  finalPaymentDate: string;
  paymentFrequency: PayFrequencyOptionValue;
  nextAutopay?: NextAutopay;
}

export type LoanBreakdownResponse = LoanBreakdownItem[];

export interface ApplicationDataApi {
  getApplicationData(applicationId: string): Promise<GetApplicationDataResponse>;
  getApplicationApr(applicationId: string): Promise<GetApplicationAprResponse>;
  createApplication(data: CreateApplicationData): Promise<CreateApplicationResponse>;
  updateApplicationBankAccountData(data: DataForUpdatingBankAccount): Promise<void>;
  updateApplicationStatus(data: DataForUpdatingApplicationStatus): Promise<ApplicationStatusUpdateResponse>;
  updateApplicationCheckingAccount(applicationId: string, bankAccountId: string): Promise<void>;
  updateApplicationFeedbackData(data: DataForUpdatingApplicationFeedback): Promise<void>;
  patchIdentity(data: DataForPatchingIdentity): Promise<void>;
  patchFlow(data: DataForPatchingFlow): Promise<void>;
  updateApplicationLoanManagementData(data: DataForUpdatingApplicationLoanManagement): Promise<void>;
  updateEmploymentData(data: UpdatedEmploymentData): Promise<GetApplicationDataResponse>;
  updateSelectedTradelines(
    applicationId: string,
    tradelinesToUpdate: SelectedTradeline[],
  ): Promise<GetApplicationDataResponse>;
  setRecommendationStrategy(
    applicationId: string,
    strategy: RecommendationStrategy,
  ): Promise<GetApplicationDataResponse>;
  updateAdditionalFunds(applicationId: string, additionalFunds: number): Promise<GetApplicationDataResponse>;
  updatePaymentMethod(applicationId: string, paymentMethod: PaymentMethod): Promise<GetApplicationDataResponse>;
  getLatestLoanPayment(applicationId: string): Promise<LatestLoanPaymentResponse>;
  getLoanBreakdown(applicationId: string): Promise<LoanBreakdownResponse>;
  getAdditionalFunds(applicationId: string): Promise<AdditionalFundsData>;
  getUpcomingLoanPayments(applicationId: string): Promise<GetUpcomingLoanPaymentsResponse>;
}

export enum RecommendationStrategy {
  OptimizeForTotalSavings = 'OptimizeForTotalSavings',
  OptimizeForMonthlyPayment = 'OptimizeForMonthlyPayment',

  /**
   * A weighted combination of savings and monthly payment should be positive.
   */
  OptimizeForCombined = 'OptimizeForCombined',

  /**
   * Either savings or monthly payment savings can be positive.
   */
  OptimizeForEither = 'OptimizeForEither',

  /**
   * Both savings and monthly payment savings should be positive.
   */
  OptimizeForBoth = 'OptimizeForBoth',
}

export default class ApplicationDataRestApi extends Api implements ApplicationDataApi {
  public async getApplicationData(applicationId: string) {
    const data = await this.fetch<GetApplicationDataResponse>(`/application/${applicationId}`, {
      method: FetchMethodType.GET,
      errorMode: ErrorMode.HideError,
    });
    return data;
  }

  public async getApplicationApr(applicationId: string) {
    const result = await this.fetch<GetApplicationAprResponse>(`/loanManagement/apr/${applicationId}`, {
      method: FetchMethodType.GET,
    });
    return result;
  }

  public async createApplication(data: CreateApplicationData) {
    const result = await this.fetch<CreateApplicationResponse>(`/application`, {
      method: FetchMethodType.POST,
      body: {
        data,
      },
    });
    return result;
  }

  public async updateApplicationBankAccountData(data: DataForUpdatingBankAccount) {
    await this.fetch<void>(`/bankAccount`, {
      method: FetchMethodType.POST,
      body: {
        data,
      },
      errorMode: ErrorMode.DisplayActualMessage,
    });
  }

  public async updateApplicationStatus(data: DataForUpdatingApplicationStatus) {
    const applicationStatus = await this.fetch<ApplicationStatusUpdateResponse>(`/updateApplicationStatus`, {
      method: FetchMethodType.POST,
      body: {
        data,
      },
    });
    return applicationStatus;
  }

  public async updateApplicationCheckingAccount(applicationId: string, bankAccountId: string): Promise<void> {
    await this.fetch<void>(`/bankAccount/primaryCheckingAccount/${applicationId}`, {
      method: FetchMethodType.PUT,
      body: {
        bankAccountId,
      },
    });
  }

  public async updateApplicationFeedbackData(data: DataForUpdatingApplicationFeedback) {
    await this.fetch<void>('/application/feedback', {
      method: FetchMethodType.PATCH,
      body: {
        data,
      },
    });
  }

  public async patchIdentity(data: DataForPatchingIdentity) {
    const { applicationId, ...values } = data;
    return this.fetch<void>(`/application/${applicationId}/identity`, {
      method: FetchMethodType.PATCH,
      body: {
        ...values,
      },
    });
  }

  public async patchFlow(data: DataForPatchingFlow) {
    const { applicationId, currentFlow } = data;
    return this.fetch<void>(`/application/${applicationId}/flow`, {
      method: FetchMethodType.PATCH,
      body: {
        currentFlow,
      },
    });
  }

  public async updateEmploymentData(data: UpdatedEmploymentData): Promise<GetApplicationDataResponse> {
    const { applicationId, employmentData } = data;
    const application = await this.fetch<GetApplicationDataResponse>(`/employers/${applicationId}`, {
      method: FetchMethodType.PUT,
      body: { ...employmentData },
    });

    return application;
  }

  public async updateSelectedTradelines(applicationId: string, tradelinesToUpdate: SelectedTradeline[]) {
    return (await this.fetch(`/reports/debtProfile/${applicationId}`, {
      method: FetchMethodType.PATCH,
      body: {
        tradelinesToUpdate,
      },
    })) as GetApplicationDataResponse;
  }

  public async setRecommendationStrategy(applicationId: string, strategy: RecommendationStrategy) {
    return (await this.fetch(`/reports/recommendationStrategy/${applicationId}`, {
      method: FetchMethodType.PATCH,
      body: {
        strategy,
      },
    })) as GetApplicationDataResponse;
  }

  public async updateAdditionalFunds(applicationId: string, additionalFundsAmount: number) {
    return (await this.fetch(`/reports/additionalFunds/${applicationId}`, {
      method: FetchMethodType.PATCH,
      body: {
        additionalFundsAmount,
      },
    })) as GetApplicationDataResponse;
  }

  public async updatePaymentMethod(applicationId: string, paymentMethod: PaymentMethod) {
    return (await this.fetch(`/reports/paymentMethod/${applicationId}`, {
      method: FetchMethodType.PATCH,
      body: {
        paymentMethod,
      },
    })) as GetApplicationDataResponse;
  }

  public async updateApplicationLoanManagementData(data: DataForUpdatingApplicationLoanManagement) {
    await this.fetch<void>(`/application/loanManagement`, {
      method: FetchMethodType.PATCH,
      body: {
        data,
      },
    });
  }

  async getLatestLoanPayment(applicationId: string): Promise<LatestLoanPaymentResponse> {
    return this.fetch<LatestLoanPaymentResponse>(`/loanManagement/latestLoanPayment/${applicationId}`, {
      method: FetchMethodType.GET,
    });
  }

  async getLoanBreakdown(applicationId: string): Promise<LoanBreakdownResponse> {
    return this.fetch<LoanBreakdownResponse>(`/loanManagement/breakdown/${applicationId}`, {
      method: FetchMethodType.GET,
    });
  }

  async getAdditionalFunds(applicationId: string): Promise<AdditionalFundsData> {
    return this.fetch<AdditionalFundsData>(`/reports/additionalFunds/${applicationId}`, {
      method: FetchMethodType.GET,
    });
  }

  async getUpcomingLoanPayments(applicationId: string): Promise<GetUpcomingLoanPaymentsResponse> {
    return this.fetch<GetUpcomingLoanPaymentsResponse>(`/loanManagement/upcomingPayments/${applicationId}`, {
      method: FetchMethodType.GET,
    });
  }
}
